/**
 * Resolves and renders artifacts by name.
 *
 * Takes a list of artifact names and fetches their content via the
 * GET /artifacts/resolve endpoint. Used for both streaming (artifact
 * names arrive via CustomEvent) and history (names from message metadata).
 *
 * Artifacts are lazy-loaded via IntersectionObserver — they only fetch
 * when the placeholder scrolls near the viewport. Resolved data is
 * stored in a module-level cache so returning to a conversation is instant.
 */
import { type FC, useState, useEffect, useRef } from "react";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { BlobFileMessage } from "~/components/assistant/messages/BlobFileMessage";
import { DataValueMessage } from "~/components/assistant/messages/DataValueMessage";
import { DataframeMessage } from "~/components/assistant/messages/DataframeMessage";
import { FigureMessage } from "~/components/assistant/messages/FigureMessage";
import { ImageMessage } from "~/components/assistant/messages/ImageMessage";
import { TextFileMessage } from "~/components/assistant/messages/TextFileMessage";
import { apiClient } from "~/utils/apiClient";

// Module-level cache: resolved artifacts survive component remounts and
// client-side navigation so returning to a conversation is instant.
const resolvedCache = new Map<string, EnrichedArtifact[]>();

interface EnrichedArtifact {
  artifact_id: string;
  artifact_name?: string;
  filename?: string;
  type: "figure" | "dataframe" | "value" | "text" | "blob" | "image" | "excel";
  content: any;
  summary?: string;
  code?: string;
}

const ARTIFACT_COMPONENTS: Record<
  EnrichedArtifact["type"],
  { Component: FC<any>; propName: string }
> = {
  figure: { Component: FigureMessage, propName: "figure" },
  dataframe: { Component: DataframeMessage, propName: "dataframe" },
  value: { Component: DataValueMessage, propName: "value" },
  text: { Component: TextFileMessage, propName: "textFile" },
  blob: { Component: BlobFileMessage, propName: "blobFile" },
  image: { Component: ImageMessage, propName: "image" },
  // This legacy surface can't render the inline sheet viewer, so excel
  // artifacts fall back to the file/download card.
  excel: { Component: BlobFileMessage, propName: "blobFile" },
};

const ArtifactRenderer: FC<{ artifact: EnrichedArtifact }> = ({ artifact }) => {
  const entry = ARTIFACT_COMPONENTS[artifact.type];
  if (!entry) {
    return null;
  }
  const { Component, propName } = entry;
  const data = {
    artifact_id: artifact.artifact_id,
    artifact_name: artifact.artifact_name,
    filename: artifact.filename,
    content: artifact.content,
    summary: artifact.summary,
    code: artifact.code,
  };
  return (
    <div className="px-2">
      <Component {...{ [propName]: data }} debugMode={false} />
    </div>
  );
};

/**
 * Skeleton placeholder matching the artifact card shell. Reserves
 * enough vertical space to prevent layout jumps when the real
 * content loads. Rendered per artifact name so multiple artifacts
 * each get their own placeholder.
 */
const ArtifactSkeleton: FC = () => (
  <div className="px-2">
    <div className="animate-pulse rounded-lg border border-gray-200 bg-white p-4 pt-1 w-full">
      {/* Tab bar skeleton */}
      <div className="flex items-center gap-3 border-b border-gray-200 py-3 mb-4">
        <div className="h-3 w-12 rounded bg-gray-200" />
        <div className="h-3 w-10 rounded bg-gray-200" />
        <div className="h-3 w-14 rounded bg-gray-200" />
      </div>
      {/* Content area — matches compact artifact min-h-48 */}
      <div className="flex min-h-48 items-center justify-center">
        <div className="h-4 w-32 rounded bg-gray-100" />
      </div>
    </div>
  </div>
);

export const ArtifactDisplay: FC<{ artifactNames: string[] }> = ({ artifactNames }) => {
  const { conversationId, api } = useAssistantContext();
  const artifactKey = artifactNames.join(",");
  const cacheKey = conversationId ? `${conversationId}:${artifactKey}` : "";

  const [enrichedArtifacts, setEnrichedArtifacts] = useState<EnrichedArtifact[]>(
    () => (cacheKey ? resolvedCache.get(cacheKey) : undefined) ?? []
  );
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!cacheKey || !conversationId) {
      return;
    }

    // Cache hit — update state (no-op if same reference) and skip fetch.
    const cached = resolvedCache.get(cacheKey);
    if (cached) {
      setEnrichedArtifacts(cached);
      return;
    }

    // Cache miss — lazy-load via IntersectionObserver.
    const el = containerRef.current;
    if (!el) {
      return;
    }

    let cancelled = false;

    const fetchArtifacts = async () => {
      if (resolvedCache.has(cacheKey)) {
        setEnrichedArtifacts(resolvedCache.get(cacheKey)!);
        return;
      }
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        for (const name of artifactNames) {
          params.append("names", name);
        }
        const resolved = await apiClient<EnrichedArtifact[]>(
          `${api.baseUrl}/conversations/${conversationId}/artifacts/resolve?${params}`
        );
        if (!cancelled) {
          resolvedCache.set(cacheKey, resolved);
          setEnrichedArtifacts(resolved);
        }
      } catch (error) {
        console.error("Error resolving artifacts:", error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          fetchArtifacts();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
    // artifactKey is derived from artifactNames — no need to list the array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, conversationId, artifactKey]);

  const showLoading =
    isLoading || (!resolvedCache.has(cacheKey) && enrichedArtifacts.length === 0 && !!cacheKey);

  return (
    <div ref={containerRef} className="w-full space-y-4 my-4">
      {enrichedArtifacts.length === 0 && showLoading
        ? artifactNames.map((name) => <ArtifactSkeleton key={name} />)
        : enrichedArtifacts.map((artifact, index) => (
            <ArtifactRenderer
              key={artifact.artifact_id || `artifact-${index}`}
              artifact={artifact}
            />
          ))}
    </div>
  );
};
